import { createRootRoute, Outlet, useRouter } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useBaseInfoCache } from "@/api/base-info";
import { hasSsoSession } from "@/auth/auth-token";
import { PageLayout } from "@/components/ui";
import { isLocal } from "@/utils";
import { bindShopifyNavigation, visibleAdminNavigation } from "../app/navigation";

const StandaloneAdminChrome = lazy(() =>
  import("../app/dev-admin-chrome").then((module) => ({
    default: module.DevAdminChrome,
  })),
);

export const Route = createRootRoute({
  component: AdminLayout,
  notFoundComponent: NotFound,
});

function NotFound() {
  const { t } = useTranslation();
  return (
    <PageLayout heading="BlyCare">
      <s-section heading={t("PageNotFound")} />
    </PageLayout>
  );
}

function AdminLayout() {
  const router = useRouter();
  const info = useBaseInfoCache();
  const { t } = useTranslation();
  const standalone = isLocal || hasSsoSession();

  useEffect(() => {
    if (standalone) return;
    return bindShopifyNavigation((href) => void router.navigate({ to: href }));
  }, [router, standalone]);

  const content = <Outlet />;

  if (standalone) {
    return (
      <Suspense fallback={content}>
        <StandaloneAdminChrome>{content}</StandaloneAdminChrome>
      </Suspense>
    );
  }

  return (
    <>
      {info ? (
        <s-app-nav>
          {visibleAdminNavigation(info.is_plus).map((item) => (
            <s-link
              key={item.href}
              href={item.href}
              {...(item.home ? { rel: "home" as const } : {})}
            >
              {t(item.titleKey)}
            </s-link>
          ))}
        </s-app-nav>
      ) : null}
      {content}
    </>
  );
}
